import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { auth, signInWithEmailAndPassword } from "../../firebase-config";
import {
  saveAdminToLocalStorage,
  getAdminFromLocalStorage,
  clearAdminLocalStorage,
} from "../../shared/localStorage";
import {
  setAdminSessionTimeout,
  clearAdminSessionTimeout,
  handleAdminSessionExpiration,
} from "../../shared/session";
import CustomToast from "../../shared/CustomToast";
import { Spinner } from "react-bootstrap";

const AdminAuthContext = createContext();

export const AdminAuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastTitle, setToastTitle] = useState("");
  const [toastBgColor, setToastBgColor] = useState("light");
  const [toastActionType, setToastActionType] = useState("");
  const [sessionExpired, setSessionExpired] = useState(false);
  const sessionTimeoutRef = useRef(null);
  const navigate = useNavigate();

  const showToastMessage = useCallback((title, message, bgColor) => {
    setToastTitle(title);
    setToastMessage(message);
    setToastBgColor(bgColor);
    setToastActionType(bgColor);
    setShowToast(true);
  }, []);

  const resetAdminSessionTimeout = useCallback(() => {
    clearAdminSessionTimeout(sessionTimeoutRef);
    if (admin && !sessionExpired) {
      sessionTimeoutRef.current = setAdminSessionTimeout(() =>
        handleAdminSessionExpiration(showToastMessage, setSessionExpired)
      );
    }
  }, [admin, sessionExpired, showToastMessage]);

  const fetchAdminData = async (uid) => {
    try {
      const adminDataResponse = await axios.get(
        `https://psikotes-server-upj.vercel.app/admin/${uid}`
      );
      const adminData = { ...adminDataResponse.data, uid };
      setAdmin(adminData);
      saveAdminToLocalStorage(adminData);
      return adminData;
    } catch (error) {
      console.error("Failed to fetch admin data:", error.message || error);
      showToastMessage(
        "Error",
        "Failed to fetch admin data. Please try again later.",
        "danger"
      );
      return null;
    } finally {
      setInitialized(true);
    }
  };

  const logout = useCallback(() => {
    clearAdminLocalStorage();
    localStorage.removeItem("adminSessionExpired");
    setIsLoggedIn(false);
    setAdmin(null);
    clearAdminSessionTimeout(sessionTimeoutRef);
    navigate("/adminJaya");
    showToastMessage(
      "Sign Out Successful",
      "You have been Sign out.",
      "success"
    );
  }, [navigate, showToastMessage]);

  const extendSession = useCallback(() => {
    setShowToast(false);
    setSessionExpired(false);
    localStorage.removeItem("adminSessionExpired");
    resetAdminSessionTimeout();
  }, [resetAdminSessionTimeout]);

  const handleCancelSession = useCallback(() => {
    clearAdminLocalStorage();
    localStorage.removeItem("adminSessionExpired");
    setIsLoggedIn(false);
    setAdmin(null);
    navigate("/adminJaya");
    setSessionExpired(false);
    clearAdminSessionTimeout(sessionTimeoutRef);
    showToastMessage(
      "Session Canceled",
      "Your session has been canceled.",
      "success"
    );
  }, [navigate, showToastMessage]);

  const signInAdmin = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const uid = userCredential.user.uid;
      const adminData = await fetchAdminData(uid);
      if (adminData) {
        setIsLoggedIn(true);
        navigate("/adminJaya/dashboard");
        resetAdminSessionTimeout();
        showToastMessage("Sign In Successful", `Welcome back, ${adminData.name}!`, "success");
      }
    } catch (error) {
      showToastMessage(
        "Login Failed",
        "Incorrect email or password. Please try again.",
        "danger"
      );
      throw error; 
    }
  };
  

  useEffect(() => {
    const { loggedIn, storedAdmin, sessionExpired } =
      getAdminFromLocalStorage();
    if (sessionExpired) {
      logout();
    } else if (loggedIn && storedAdmin) {
      setIsLoggedIn(true);
      setAdmin(storedAdmin);
    }
    setInitialized(true);
  }, [logout]);

  useEffect(() => {
    const handleUserActivity = () => {
      if (!sessionExpired) {
        resetAdminSessionTimeout();
      }
    };

    const activityEvents = ["mousemove", "mousedown", "keydown", "touchstart"];

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleUserActivity);
    });

    resetAdminSessionTimeout();
    return () => {
      clearAdminSessionTimeout(sessionTimeoutRef);
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [admin, sessionExpired, resetAdminSessionTimeout]);

  useEffect(() => {
    const { sessionExpired } = getAdminFromLocalStorage();
    if (sessionExpired) {
      logout();
    }
  }, [logout]);

  if (!initialized) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(128, 128, 128, 0.5)",
    zIndex: 4,
    display: sessionExpired ? "block" : "none",
  };

  return (
    <AdminAuthContext.Provider
      value={{
        isLoggedIn,
        admin,
        logout,
        signInAdmin,
        initialized,
      }}
    >
      <div style={overlayStyle}></div>
      {children}
      <CustomToast
        showToast={showToast}
        setShowToast={setShowToast}
        title={toastTitle}
        body={toastMessage}
        bgColor={toastBgColor}
        actionType={toastActionType}
        onExtendSession={
          toastActionType === "warning" ? extendSession : undefined
        }
        onCancelSession={
          toastActionType === "warning" ? handleCancelSession : undefined
        }
      />
    </AdminAuthContext.Provider>
  );
};

export default AdminAuthContext;
